import { useCallback, useEffect, useRef, useState } from "react";

import { useDevTools } from "./ui/useDevTools";

import type { GameStore } from "src/engine/game/GameStore";
import { exposeForTests } from "src/engine/game/debugHook";
import { loadName } from "src/client/ai/ui/playerName";
import { GatePage } from "src/client/ai/ui/GatePage";
import { LandingPage, SignInDoor } from "src/client/ai/ui/LandingPage";
import { TermsPage } from "src/client/ai/ui/TermsPage";
import { PrivacyPage } from "src/client/ai/ui/PrivacyPage";
import { RulesPage } from "src/client/ai/ui/RulesPage";
import { ADMIN_PAGES, type AdminPage } from "src/client/ai/ui/AdminNav";
import { FeaturesPage } from "src/client/ai/ui/FeaturesPage";
import { TunerPage } from "src/client/ai/ui/TunerPage";
import { UsersPage } from "src/client/ai/ui/UsersPage";
import { WatchCpuPage } from "src/client/ai/ui/WatchCpuPage";
import type { RulesetChoice } from "src/engine/rules/ruleset";
import { type LevelSource, loadLevelSource, loadRunKindLevel } from "src/engine/campaign/levels";
import { RUN_CONFIG, type RunKind, runKindOfState, runOpening, TRAINING_LEVEL } from "src/engine/game/modes";
import { clearRun, loadRun, saveRun, savedRun } from "src/engine/game/runSave";
import { freshSeed } from "src/engine/game/seed";
import type { BoardMapId } from "src/engine/rules/maps";
import { scenarioById, scenarioOpening } from "src/engine/ai/scenarios";
import { useDefaultRuleset } from "src/client/ai/hooks/useRuleset";
import { GameStoreContext } from "src/client/ai/hooks/useGameStore";
import type { EngineGame } from "src/engine/api/createGame";
import { GameContext } from "./ui/useGame";
import { useClientSwitchButton } from "src/client/clientSwitchContext";
import { useExperimentsBadge } from "src/client/experimentsBadgeContext";
import { styled } from "src/client/ai/ui/kit";
import { useKept, useKeptRef } from "./ui/useKept";
import { RoomClient } from "src/engine/net/RoomClient";
import type { Seat } from "src/engine/net/protocol";
import { NetContext } from "src/client/ai/hooks/useNet";
import { GameShell } from "src/client/ai/ui/GameShell";
import { runIsOver } from "src/client/ai/ui/defeat";
import type { Level, Tuning } from "src/engine/ai/difficulty";
import { type LaunchMode, type LaunchOptions, ModeMenu } from "src/client/ai/ui/ModeMenu";
import { ScenarioScreen } from "src/client/ai/ui/ScenarioScreen";
import { ConsentDialog } from "src/client/ai/ui/ConsentDialog";
import { useConsent } from "src/client/ai/ui/useConsent";
import { gameIsFinished, kindOf } from "src/engine/telemetry/finished";
import { milestoneOf, RUN_EVERY, track } from "src/engine/telemetry/analytics";
import { runReport } from "src/engine/telemetry/runReport";
import { runIsWon } from "src/engine/rules/campaign/objectives";
import { EMPTY_TALLY } from "src/engine/rules/tally";
import { isGameOver } from "src/engine/rules/chess/status";
import { stagesCleared } from "src/engine/ai/waves";
import { submitGame } from "src/engine/telemetry/submit";

/**
 * `?room=<id>` puts the app online; without it the game is local hot-seat.
 * `?seat=` is a hint only — the server assigns seats, first come first served.
 */
/** Where the tuning dashboard lives. */
const TUNER_PATH = "/tuner";
/**
 * Where the rollout board lives.
 *
 * A path for `TUNER_PATH`'s reason — a screen you go to rather than a panel
 * over a board it has nothing to do with — and behind the *same* door: one
 * audience, one password, one check (`TunerGate`).
 */
const FEATURES_PATH = "/features";
/** The approval screen. Kept in step with `ADMIN_PAGES`, which is the list. */
const USERS_PATH = "/users";
/**
 * The two documents that must be readable by anybody. §25d.
 *
 * On their own URLs because a legal notice you can only reach by clicking
 * something inside an app is one nobody can link to, cite, or check before
 * deciding whether to sign up — and because "where is your privacy policy" is
 * a question that expects a URL for an answer.
 */
const PRIVACY_PATH = "/privacy";
const TERMS_PATH = "/tos";

/**
 * Where two machines play for an audience.
 *
 * A path for `TUNER_PATH`'s reason — a separate screen you go to rather than an
 * invitation riding on the address you were already on — and it takes an
 * optional `?seed=`, which is what makes a link to one of these games a link to
 * *that* game rather than to another random one.
 */
const WATCH_CPU_PATH = "/watch-cpu";

function roomFromUrl(): { room?: string; seat?: Seat } {
  const params = new URLSearchParams(window.location.search);
  const room = params.get("room") ?? undefined;
  const seat = params.get("seat") as Seat | null;
  return { room, seat: seat ?? undefined };
}

// A run is a solo game with a level in it, so it shares the solo shell —
// what differs is that `state.run` exists, and the shell reads that rather
// than being told twice.
// "watch" is "solo" with nobody in the seat: the same scenario, the same
// driver, both armies played by the machine. A separate mode rather than an
// option on solo because the shell has to know it holds no seat, and that is
// the one thing a scenario id cannot say.
// And "watch-run" is `run` with nobody in the seat, for the same reason: the
// machine plays the whole single-player loop — level, objectives, camps — and
// the save is never touched, because none of it is the player's.
type Mode =
  | "menu"
  | "scenarios"
  | "local"
  | "net"
  | "solo"
  | "run"
  | "training"
  | "watch"
  | "watch-run";

/** The modes that are a run, played or watched. */
const isRun = (mode: Mode): boolean =>
  mode === "run" || mode === "training" || mode === "watch-run";

/**
 * What a run opens on: a level (Training's, or one the dev panel handed over)
 * or a kind with no level — Normal Mode's generated map (§31 G3). A constant,
 * so the loader's latch compares it by identity.
 */
type RunSource = LevelSource | { kind: RunKind };
const NORMAL_RUN: RunSource = { kind: "normal" };
const isKindSource = (source: RunSource): source is { kind: RunKind } =>
  typeof source === "object" && "kind" in source;
/** The two the machine plays every side of. */
const isWatching = (mode: Mode): boolean => mode === "watch" || mode === "watch-run";

/**
 * The AI client, over the one store and the one `Game` the composition root
 * built (`src/main.tsx`).
 *
 * **Neither is made here**, so the game survives this tree: `clientSwitch.tsx`
 * swaps the whole client and the store it swaps around is the same object
 * either client reads — a board played in one is the board the other shows.
 * (The store used to be minted in a `useState` here and the `Game` on every
 * render, which leaked a session subscription per render and dropped the game
 * on a switch.)
 */
export function App(props: { store: GameStore; game: EngineGame }) {
  // Beside the screens rather than in them: `Screens` is a stack of early
  // returns, and the A/B badge and the cutover button belong on every one.
  const experiments = useExperimentsBadge();
  const clientSwitch = useClientSwitchButton();
  return (
    <>
      <Screens {...props} />
      <DevCorner>
        {experiments}
        {clientSwitch}
      </DevCorner>
    </>
  );
}

/**
 * The dev corner: the A/B badge and the cutover button, side by side. Fixed,
 * so nothing in the shell changes size with it. `z.dev` — the dev panel's rung.
 * Bottom-aligned, so the badge's sheet opens upward and the buttons stay put.
 */
const DevCorner = styled("div")(({ theme }) => ({
  position: "fixed",
  right: theme.kit.space.lg,
  bottom: theme.kit.space.lg,
  zIndex: theme.kit.z.dev,
  display: "flex",
  alignItems: "flex-end",
  gap: theme.kit.space.sm
}));

function Screens({ store, game }: { store: GameStore; game: EngineGame }) {
  const [initial] = useState(roomFromUrl);
  // The role's answer reaches the engine: a `Dev` account on a deployment gets
  // `game.dev` as a dev build does (not a security boundary — `DevHandle`).
  const devTools = useDevTools();
  useEffect(() => game.allowDev(devTools), [game, devTools]);
  // A link with a room in it *is* an invitation, so it skips the menu: the
  // person who was sent it wants to be in that game, not choosing a mode.
  const [mode, setMode] = useKept<Mode>(game, "mode", initial.room ? "net" : "menu");
  const [room, setRoom] = useKept(game, "room", initial.room);
  const [client, setClient] = useState<RoomClient>();
  /**
   * The board this client asked for when it *created* a room.
   *
   * Set from the menu and never from a link: joining replays the board out of
   * the room's log, so a joiner has none and sends no opening command. It is
   * dropped by a reload for the same reason — a tab that comes back to a room
   * is rejoining one, whatever it did to start it.
   */
  const [openMap, setOpenMap] = useKept<BoardMapId | undefined>(game, "openMap", undefined);

  useEffect(() => {
    if (mode !== "net" || !room) return;
    const instance = new RoomClient(store, room, initial.seat, loadName());
    instance.connect();
    setClient(instance);
    exposeForTests({ net: instance });
    return () => {
      instance.close();
      setClient(undefined);
    };
  }, [mode, room, initial.seat, store]);
  // The session sends through the room and sits in its seat while one is up.
  useEffect(() => {
    game.attachRoom(client ?? null);
    return () => game.attachRoom(null);
  }, [game, client]);

  /**
   * Open the room on the board its creator chose, once.
   *
   * **The board is a command, not a room setting**, which is what makes a
   * joiner need nothing: the map is the first entry in the log and everybody
   * who ever loads that room replays it, including this tab after a reload.
   *
   * Three guards, and each covers a different way of opening a game twice. The
   * latch covers the reconnect a slept laptop produces; `log.length` covers
   * joining a room that has already started, where an opening command would
   * throw away somebody's game; and the classic map sends nothing at all,
   * because that is the board the store already opened on and a command that
   * changes nothing is still a command that resets the board.
   */
  const openedRoom = useKeptRef(game, "openedRoom", false);
  /**
   * The ruleset a fresh game opens on, `undefined` outside every experiment —
   * the flags choose the default and the value rides in the opening command,
   * so a joiner replays the creator's game whatever their own flags say.
   */
  const ruleset = useDefaultRuleset();
  const openingRuleset: RulesetChoice | undefined = ruleset;
  useEffect(() => {
    // Every map opens by name, the classic one too — named, it is the versus
    // opening with its cobble rows (§31 Q13).
    if (!client || !openMap) return;
    const open = () => {
      if (openedRoom.current || client.getState().status !== "online") return;
      // `joined` loads the log before it reports online, so this is the room's
      // real history rather than an empty store waiting for one.
      if (store.getSnapshot().log.length > 0) {
        openedRoom.current = true;
        return;
      }
      openedRoom.current = client.send({
        type: "new-game",
        map: openMap,
        seed: freshSeed(),
        ruleset: openingRuleset
      });
    };
    open();
    return client.subscribe(open);
  }, [client, openMap, openedRoom, openingRuleset, store]);

  /**
   * Whether the deployment's password has been answered.
   *
   * `undefined` while we are still asking, and the app renders nothing in that
   * moment on purpose: flashing the menu and then replacing it with a gate
   * looks like the site changed its mind about letting you in.
   */
  const [locked, setLocked] = useState<boolean | undefined>(undefined);
  /**
   * Is there a gate at all — as against "are we through it".
   *
   * `locked` is false in two very different situations: a deployment whose
   * password we have answered, and a machine with no password at all. Telling
   * them apart is what "for authenticated games" needs, because the second is
   * every developer's laptop and every e2e smoke, and neither should be asked
   * to consent to anything or be capable of submitting. The server takes the
   * same position from the other side — `unlocked()` answers true when the
   * gate is off — so this is the half that keeps a local run quiet.
   */
  const [gated, setGated] = useState(false);
  /**
   * Whether this deployment is invite-only, and where this person stands. §25d.
   *
   * **Read from the server, never from the `require-account` flag** (§25.11):
   * the flag says what the deployment wants and this says what is actually
   * configured, and only the server knows whether there are accounts at all.
   * Null until asked, which `locked === undefined` already covers.
   */
  const [access, setAccess] = useState<{
    accounts: boolean;
    unlocked: boolean;
    signedIn: boolean;
    email: string | null;
    mail: boolean;
  } | null>(null);
  useEffect(() => {
    let live = true;
    fetch("/api/session")
      .then((response) => response.json())
      .then((session: {
        locked?: boolean;
        unlocked?: boolean;
        accounts?: boolean;
        signedIn?: boolean;
        email?: string | null;
        mail?: boolean;
      }) => {
        if (!live) return;
        setLocked(Boolean(session.locked) && !session.unlocked);
        setGated(Boolean(session.locked));
        setAccess({
          accounts: Boolean(session.accounts),
          unlocked: Boolean(session.unlocked),
          signedIn: Boolean(session.signedIn),
          email: session.email ?? null,
          mail: Boolean(session.mail)
        });
      })
      // No answer means no gate we can prove: let the player through and let
      // the socket refuse if there really is one. A dev server with no
      // `/api/session` at all is the common case here.
      .catch(() => live && setLocked(false));
    return () => {
      live = false;
    };
  }, []);

  const [rules, setRules] = useState(false);
  /** Is the privacy notice open? Its own page, for `PrivacyPage`'s reason. */
  const [privacy, setPrivacy] = useState(() => window.location.pathname === PRIVACY_PATH);
  /** The terms, on the same terms as the privacy notice beside it. */
  const [terms, setTerms] = useState(() => window.location.pathname === TERMS_PATH);
  /** Which heading to open the rules at, when a hint sent the reader there. */
  const [rulesAt, setRulesAt] = useState<string | undefined>(undefined);
  /**
   * `/tuner` is the tuning dashboard.
   *
   * A **path** rather than a query parameter, unlike `?room=`: that one is an
   * invitation to a game and rides on whatever address somebody was already
   * on, where this is a separate screen you go to. The server falls
   * extensionless paths through to the app, so it needs no route table — see
   * "a static miss is a 404, never index.html".
   */
  const [tuner, setTuner] = useState(() => window.location.pathname === TUNER_PATH);
  /** `/features` — see `FEATURES_PATH`. */
  const [features, setFeatures] = useState(() => window.location.pathname === FEATURES_PATH);
  /** The approval screen, on the same terms as the two above it. §25f. */
  const [users, setUsers] = useState(() => window.location.pathname === USERS_PATH);
  /** `/watch-cpu` — see `WATCH_CPU_PATH`. */
  const [watchCpu, setWatchCpu] = useState(() => window.location.pathname === WATCH_CPU_PATH);
  /**
   * Hand-set aggression and greed, from the scenario screen.
   *
   * Held here rather than in the shell because it is a *setup* choice, made
   * before the board exists and unchanged for the game's life — the same shelf
   * the scenario id and the difficulty sit on.
   */
  const [tuning, setTuning] = useKept<Tuning>(game, "tuning", {});
  /**
   * Which team each army is on, from the scenario screen.
   *
   * Here for the reason `tuning` is: it is board setup, and Restart
   * re-rolls the scenario from this shelf rather than from the screen.
   */
  const [teams, setTeams] = useKept<readonly number[] | undefined>(game, "teams", undefined);
  /** A seed the player asked for, so a game can be replayed exactly. */
  const [seed, setSeed] = useKept<number | undefined>(game, "seed", undefined);
  /**
   * A ceiling on how often the board draws, from the scenario screen.
   *
   * Here for the reason `tuning` is: it is launch setup, so a
   * Restart has to hand back the same one. It survives into a **watched run**
   * as well as a scenario, unlike the army picks — those are about a board and
   * a run has none, where this is about a machine playing unattended, which is
   * exactly what a watched run is.
   */
  const [maxFps, setMaxFps] = useKept<number | undefined>(game, "maxFps", undefined);
  /**
   * The authored level a run is playing, or undefined.
   *
   * Loaded in an effect rather than in the launch handler because parsing a
   * map is asynchronous and a menu button must not wait on a fetch — and
   * because a level that fails to load has to say so somewhere the player can
   * read it, rather than leaving a dead screen.
   */
  const [runLevel, keepRunLevel] = useKept<RunSource | undefined>(game, "runLevel", undefined);
  /**
   * The level the loader last put in the store, or undefined.
   *
   * **The one latch a client switch needs.** `runLevel` survives the remount
   * (`useKept`), so the loader effect below re-runs on the new tree with the
   * same source — and without this it would open a fresh run over the one in
   * play. Cleared by every explicit `setRunLevel`, so Restart (clear and set
   * the same id) and a new launch load exactly as before; only a mount that
   * set nothing is skipped.
   */
  const loadedLevel = useKeptRef<RunSource | undefined>(game, "loadedLevel", undefined);
  const setRunLevel = useCallback(
    (source: RunSource | undefined) => {
      loadedLevel.current = undefined;
      keepRunLevel(source);
    },
    [keepRunLevel, loadedLevel]
  );
  const [levelError, setLevelError] = useState<string>();
  /**
   * A sentence the shell owes the player as it opens, or undefined (§29 U5).
   *
   * Set by `launch` and by nothing else, so it is latched by construction: the
   * pill says a notice once per text and this state changes only when a mode
   * does. Cleared on every launch, since it is about the game being opened.
   */
  const [notice, setNotice] = useKept<string | undefined>(game, "notice", undefined);
  /**
   * Has the level actually arrived in the store?
   *
   * The board is not drawn until it has. A `GameStore` opens on an ordinary
   * chess position, so without this the first thing a run showed was the
   * classic eight-by-eight — framed, lit and wrong — cutting to the real
   * ground a tick later.
   */
  const [hasLevel, setHasLevel] = useKept(game, "hasLevel", false);
  useEffect(() => {
    // By value, not identity: a dev refresh (`devResume.ts`) brings both back
    // as fresh objects, and only `setRunLevel` clears the latch.
    if (!runLevel || JSON.stringify(runLevel) === JSON.stringify(loadedLevel.current)) return;
    let live = true;
    // A restart clears and re-sets `runLevel`, so this has to go back to false
    // rather than being set once: the second load would otherwise render the
    // previous level's board while the new one was still coming.
    setHasLevel(false);
    // **A kind of run opens off its mode row** (§31 G1, G3): Normal Mode
    // composes its fragments, Training names a level. A level the dev panel
    // handed over is the old run on that level, as it always was.
    const source = runLevel;
    const kind: RunKind | undefined = isKindSource(source)
      ? source.kind
      : source === TRAINING_LEVEL
        ? "training"
        : undefined;
    (isKindSource(source) ? loadRunKindLevel(source.kind) : loadLevelSource(source))
      .then((level) => {
        if (!live) return;
        // Seeded here, not in the rules: a run that always rolled the same
        // numbers is a run you play the same way twice.
        //
        // **One picker, two dials** (§26 F13). The difficulty chosen in the
        // menu sets the skill ceiling the run climbs to *and*, at hard, the
        // run's own constitution — despair's grace period, the spawn clock,
        // how strong a camp arrives. Read off the ref rather than the state
        // so this effect is not re-created when the difficulty changes:
        // Continue sets it without setting `runLevel`, and a dependency here
        // would re-open a fresh run over the restored one.
        const hard = difficulty.current === "hard";
        store.dispatch(
          kind
            ? runOpening(kind, { seed: seed ?? freshSeed(), hard, level })
            : {
                type: "new-game",
                seed: seed ?? freshSeed(),
                run: [],
                level,
                ruleset: openingRuleset,
                mode: hard ? "hard" : undefined
              }
        );
        loadedLevel.current = runLevel;
        setHasLevel(true);
      })
      .catch((error: unknown) => {
        const why = error instanceof Error ? error.message : String(error);
        // Loudly, and in two places: a level that will not load has to say so
        // where a player can read it *and* where whoever is authoring the map
        // is looking, which is the console.
        console.error("level failed to load:", why);
        if (live) {
          setLevelError(why);
          setRunLevel(undefined);
          setMode("menu");
        }
      });
    return () => {
      live = false;
    };
    // `seed` is read above: a run opened with a typed seed starts from *that*
    // roll, and the missing-dependency rule has caught the opponent stalling
    // three times. `openingRuleset` is memoised or set beside `runLevel`, so
    // neither can change under a run in play and re-open one.
  }, [loadedLevel, openingRuleset, runLevel, seed, setHasLevel, setMode, setRunLevel, store]);
  /**
   * Keep the run saved, and only a run.
   *
   * Subscribed to the store rather than driven from the launch handler,
   * because everything that changes a run goes through the store — a move, a
   * card, a belt, the opponent's turn, a stage boundary — and any other hook
   * would be a list of those to keep in step. `state.run` being present is
   * what makes a game a run, so it is also what decides whether to write.
   *
   * The whole log every time. It is a few KB, and a save that appended would
   * have to reason about a log that was rebuilt rather than extended, which
   * `load` and `next-stage` both do.
   */
  const [scenario, setScenario] = useKept<string | undefined>(game, "scenario", undefined);
  const [level, setLevel] = useKept<Level>(game, "level", "normal");
  /**
   * The same value, readable from the save subscription below.
   *
   * That callback is memoised on the store alone and has to stay that way — it
   * *subscribes*, so re-creating it whenever the difficulty changed would tear
   * the subscription down and build it back up for a setting it only reads.
   */
  const difficulty = useRef(level);
  difficulty.current = level;
  /**
   * Is this run the player's?
   *
   * A ref for the reason `difficulty` is one: the subscription below is built
   * once, and re-creating it whenever the mode changed would tear the
   * subscription down and build it back up for something it only reads.
   *
   * **A watched run must never touch the save.** It is a run in every other
   * respect — the same level, the same objectives, the same camps — so without
   * this, sitting down to watch one overwrites the run somebody was playing,
   * and Continue leads to the machine's board rather than theirs.
   */
  const ownRun = useRef(true);
  ownRun.current = mode !== "watch-run";

  useEffect(
    () =>
      store.subscribe(() => {
        const { state, log } = store.getSnapshot();
        if (!state.run || !ownRun.current) return;

        // A lost run is not a run to come back to.
        //
        // The same test the stage boundary uses, pointed the other way: a side
        // that cannot move has been beaten, and when that side is *yours* the
        // run is over. Leaving the save would offer Continue on a board where
        // the only thing left to do is look at it.
        //
        // `runIsOver` rather than the test written out here, because the shell
        // asks the same question to decide whether to *say* the run is over —
        // and a game that has quietly thrown the save away while the board
        // still looks playable is the worst version of this.
        //
        // **Won counts too.** Offering Continue into a board whose last
        // objective is already done is a run that cannot be played and cannot
        // be finished, which is the same dead end losing used to leave.
        if (runIsOver(state)) {
          clearRun();
          return;
        }

        // **The log rides along** (§29 U5), so undo and the history preview
        // survive a reload. It is still only a hint: `loadRun` replays it and
        // checks it against the snapshot's hash before it is believed.
        saveRun(state, state.run.level?.levelName ?? RUN_CONFIG.normal.name, difficulty.current, log);
      }),
    [store]
  );

  /**
   * Send a finished game, if this browser said we may.
   *
   * **The consent is read at the moment of sending, never captured.** A player
   * who withdraws mid-game must not have that game sent by a closure made when
   * they had not, which is why `consent.current()` is a function rather than
   * the value from the render this effect was created in.
   *
   * **Latched on the log's identity, not on a boolean.** A finished board
   * emits further snapshots — the shell reads it, the camera settles, an
   * overlay mounts — and every one of them would resend. A boolean would also
   * have to be cleared on a new game, which is a second thing to get right; the
   * log array is replaced whenever a game begins, so keying on it is
   * self-clearing. `log.length` rides along because undo is
   * truncate-and-replay and hands back a *shorter* log for the same game.
   *
   * **Watching is submitted and hot-seat is not distinguished from play**,
   * both deliberate: `kindOf` files them separately so a reader can exclude
   * machine games, rather than this effect deciding for them.
   */
  const consent = useConsent();
  const sent = useRef<readonly unknown[] | null>(null);
  useEffect(
    () =>
      store.subscribe(() => {
        const { state, log } = store.getSnapshot();
        // No gate, no submission — a developer's laptop and every e2e smoke
        // reach here with `unlocked()` answering true on the server, so this is
        // the check that keeps local play out of the collection entirely.
        if (!gated) return;
        const kind = kindOf(mode);
        if (!kind || log.length === 0) return;
        if (sent.current === log) return;
        // A run's ending is the shell's own question, and asking it here would
        // be a second reading of "is this run over" that could disagree with
        // the plate the player is looking at.
        if (!gameIsFinished(state, runIsOver(state))) return;
        sent.current = log;
        void submitGame({
          consent: consent.current(),
          kind,
          log,
          build: __BUILD_ID__
        });
      }),
    // `mode` is read inside, so the subscription is rebuilt when it changes —
    // which is right: a game that ends after the player has left for the menu
    // is not a game to file under the menu.
    [store, mode, consent, gated]
  );

  /**
   * How far the current run has been reported to, in `RUN_EVERY`-ply steps.
   *
   * A ref rather than state: nothing renders from it, and it must survive the
   * re-render every command causes. Reset in `launch`, so continuing a run
   * reports from where that run *is* rather than replaying its whole history
   * as a burst of milestones the moment it loads.
   */
  const milestone = useKeptRef(game, "milestone", 0);

  /**
   * What is being played, as analytics sees it.
   *
   * **Every parameter here is a number or a word from a list this file owns.**
   * Nothing a player typed, nothing another player owns — the room code most of
   * all, which the consent screen names as belonging to whoever you are playing
   * as much as to you.
   *
   * A run reports its pace and multiplayer reports its ends, because those are
   * the two questions the property exists to answer: do people get past the
   * first ten minutes of a run, and does a game somebody was sent a link to
   * actually get played.
   */
  useEffect(
    () =>
      store.subscribe(() => {
        const { state, log } = store.getSnapshot();
        if (!isRun(mode) || mode === "watch-run") return;
        const at = milestoneOf(state.history.length);
        // Strictly further on, never merely different: undo is
        // truncate-and-replay, so a run's ply count goes backwards and a
        // boundary crossed twice would otherwise be reported twice.
        if (at <= milestone.current) return;
        milestone.current = at;
        track("run_progress", {
          plies: at * RUN_EVERY,
          // How many camps have been beaten, which is the thing a pace number
          // is worth reading against.
          front: stagesCleared(state) + 1,
          commands: log.length
        });
      }),
    [store, mode, milestone]
  );

  /**
   * The same pace signal for the two modes that had none.
   *
   * **A versus game reported its start and its end and nothing in between**, so
   * a game somebody abandoned at ply forty produced no row at all — and "nobody
   * finishes a two-player game" could not be told from "nobody starts one",
   * which is the more interesting question for a prototype. A scenario reported
   * nothing whatever, and it is a whole door on the front page.
   *
   * **Its own event rather than widening `run_progress`.** That one is already
   * collecting and its rows carry no `mode`, so folding these in would either
   * split its history or make every existing row ambiguous. Two names union
   * cleanly in a funnel; a renamed event does not.
   *
   * Watched games are excluded for the reason a watched run is: nobody is
   * playing them.
   */
  const gameMilestone = useRef(0);
  useEffect(() => {
    gameMilestone.current = 0;
  }, [mode, room]);
  useEffect(
    () =>
      store.subscribe(() => {
        const { state, log } = store.getSnapshot();
        // The three a person plays that are not a run: a room, hot-seat, and
        // a scenario against the machine. `watch` and `watch-run` are excluded
        // by not being listed — nobody is playing those.
        if (mode !== "net" && mode !== "local" && mode !== "solo") return;
        const at = milestoneOf(state.history.length);
        // Strictly further on, exactly as the run's is — a board can go
        // backwards and a boundary crossed twice must report once.
        if (at <= gameMilestone.current) return;
        gameMilestone.current = at;
        track("game_progress", {
          mode,
          plies: at * RUN_EVERY,
          commands: log.length
        });
      }),
    [store, mode, room]
  );

  /**
   * A front cleared — a *round*, as against the run.
   *
   * **The run's own scorecard, at the one moment it is complete.** `next-stage`
   * rolls `tally` into `lastStage` as the boundary lands, so the front just
   * closed is sitting there fully counted and about to be replaced by the next
   * one's — which is why this reads `lastStage` rather than `tally`, and why it
   * has to be watched for rather than asked at the end.
   *
   * Latched on the count of cleared fronts, so it is one event per boundary
   * however many times the store settles afterwards.
   */
  const fronts = useKeptRef(game, "fronts", 0);
  useEffect(
    () =>
      store.subscribe(() => {
        const { state } = store.getSnapshot();
        if (!isRun(mode) || mode === "watch-run" || !state.run) return;
        const cleared = stagesCleared(state);
        if (cleared <= fronts.current) return;
        fronts.current = cleared;
        const closed = state.lastStage ?? EMPTY_TALLY;
        track("run_front_cleared", {
          front: cleared,
          plies: state.history.length,
          moves: closed.moves,
          taken: closed.taken,
          score: closed.score,
          mined: closed.mined,
          difficulty: level
        });
      }),
    [store, mode, level, fronts]
  );

  /**
   * A finished run, counted.
   *
   * **Every field is a count and none of them is the game**, which is the whole
   * difference between this and the consented submission next door: that one
   * sends a log and can be replayed move by move, and this cannot be replayed
   * at all. `runReport` is where the fields and that argument live.
   *
   * Once per run, latched on a ref rather than on the log — a continued run
   * has almost no log, so the log's identity says nothing useful here.
   */
  const reported = useKeptRef(game, "reported", false);
  useEffect(
    () =>
      store.subscribe(() => {
        const { state } = store.getSnapshot();
        if (!isRun(mode) || mode === "watch-run" || reported.current) return;
        if (!state.run || !runIsOver(state)) return;
        reported.current = true;
        track(
          "run_finished",
          runReport(state, {
            outcome: runIsWon(state) ? "won" : "lost",
            difficulty: level,
            // The run clock, which is the save's rather than the board's — see
            // `game/runSave.ts`, where it is accumulated because `src/engine/rules/`
            // may not read a clock at all.
            played: savedRun()?.played
          })
        );
      }),
    [store, mode, level, reported]
  );

  /** A room joined, reported once — a rejoin after a reload is not a new game. */
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== "net" || !room || started.current === room) return;
    started.current = room;
    // The seat and nothing else: which side somebody took is about the game,
    // where the code is about the two people in it.
    track("multiplayer_start", { seat: initial.seat ?? "unassigned" });
  }, [mode, room, initial.seat]);

  /** And its end, latched on the log so a settling board reports once. */
  const finished = useRef<readonly unknown[] | null>(null);
  useEffect(
    () =>
      store.subscribe(() => {
        const { state, log } = store.getSnapshot();
        if (mode !== "net" || log.length === 0 || finished.current === log) return;
        if (!isGameOver(state.status)) return;
        finished.current = log;
        track("multiplayer_finish", { status: state.status, plies: state.history.length });
      }),
    [store, mode]
  );

  const launch = useCallback((next: LaunchMode, options: LaunchOptions = {}) => {
    // Whatever is being opened, it is not the run whose milestones were being
    // counted a moment ago.
    milestone.current = 0;
    reported.current = false;
    fronts.current = 0;
    // Whatever the shell owed the last game, it does not owe this one.
    setNotice(undefined);
    // **The scenario screen's setup belongs to the scenario screen.** It is
    // set from `onStart` immediately before this runs and nothing ever cleared
    // it, so looking at that screen and then starting a run handed the run
    // whichever profiles and sliders were left on it — a setup choice leaking
    // into a mode that has no way to show it, let alone change it. Cleared for
    // every mode *except* the two that screen launches, because those two are
    // the setup arriving.
    if (next !== "solo" && next !== "watch") {
      setTuning({});
      setTeams(undefined);
    }
    // **Cleared on a wider set than the block above**, and the difference is
    // what each one is about. Those are picks about a *board*, which a run does
    // not have — so `watch-run` throws them away. This is about a machine
    // playing with nobody watching every move, which a watched run is more of
    // rather than less, so it survives that door and no other.
    if (next !== "solo" && next !== "watch" && next !== "watch-run") setMaxFps(undefined);
    if (next === "continue") {
      const loaded = loadRun();
      if ("failed" in loaded) {
        // Say which, and drop the save rather than leaving a Continue button
        // that fails every time it is pressed.
        setLevelError(
          loaded.failed === "no-save"
            ? "There is no saved run to continue."
            : "That saved run could not be read, so it has been cleared."
        );
        clearRun();
        setMode("menu");
        return;
      }
      // **A verified log goes in as a log** (§29 U5), which is what leaves
      // undo's floor and the history preview correct: `load` replays from
      // `newGame()`, rebuilds the step list and sets the base the same replay
      // measured. Without one — an older save, or a log the rules have moved
      // under — the snapshot lands alone exactly as it always did, and the
      // second of those is said once.
      if (loaded.log) store.load(loaded.log);
      else store.restore(loaded.state);
      if (loaded.historyDropped) {
        setNotice("The game changed since this run was saved; its history starts here.");
      }
      /**
       * **A resumed run is somebody still playing, and it looked like nothing.**
       * A Continue lands either an *empty* log (`restore`) or the whole run's
       * (a verified `load`), and either way the milestone effect starts
       * counting from nought, so the first ten plies after a Continue report
       * as a fresh run's first ten. Without this row there is no way to
       * tell a run being picked up from one being started, which is most of
       * what "do people come back" means.
       *
       * The stage is read from the state rather than the log for the reason
       * `stagesCleared` exists at all: a restored run has no log to count
       * `next-stage` commands in, and counting them was how a five-front run
       * came back reading as stage one.
       */
      track("run_resumed", {
        front: stagesCleared(loaded.state) + 1,
        plies: loaded.state.history.length,
        difficulty: loaded.difficulty ?? "unknown",
        // The run clock is the *save's*, not the board's — `src/engine/rules/` may
        // not read a clock at all, so it is accumulated in `runSave.ts`.
        played: savedRun()?.played ?? 0
      });
      // A save written before difficulty was kept has none; the menu's own
      // default is the honest answer for it, and it is the level the run was
      // most likely started at.
      setLevel(loaded.difficulty ?? "normal");
      // The saved run's own mode row (§31 G1): Training keeps its undo.
      const kept = runKindOfState(loaded.state) === "training" ? "training" : "run";
      store.setMode(kept);
      setMode(kept);
      return;
    }
    // The mode row Undo obeys (§31.1 Q2, G1).
    store.setMode(next);
    if (next === "run" || next === "watch-run") {
      // Normal Mode either way (§31 G3): what differs is who is in the seat.
      setLevel(options.level ?? "normal");
      setRunLevel(NORMAL_RUN);
    }
    if (next === "training") {
      setLevel(options.level ?? "normal");
      setRunLevel(TRAINING_LEVEL);
    }
    if (next === "solo" || next === "watch") {
      setScenario(options.scenario);
      setTeams(options.teams);
      setLevel(options.level ?? "hard");
    }
    // Kept, so Restart re-rolls the same game rather than a different one: a
    // seed you typed in is a seed you want back.
    if (next === "solo" || next === "watch" || isRun(next)) setSeed(options.seed);
    // **Which board, for the two modes that open one.** A room's creator
    // carries it to the opening command the effect above sends; a hot seat has
    // no room to wait for, so it opens right here. Cleared on every other
    // launch, or a Factory Map room left behind would decide what a run's
    // Restart hands back.
    setOpenMap(next === "net" ? options.map : undefined);
    openedRoom.current = false;
    if (next === "local" && options.map) {
      store.dispatch({ type: "new-game", map: options.map, seed: freshSeed() });
    }
    if (next === "net" && options.room) {
      // Put the room in the URL so the address bar is the share link, and so a
      // reload rejoins rather than dropping back to the menu.
      const url = new URL(window.location.href);
      url.searchParams.set("room", options.room);
      window.history.replaceState(null, "", url);
      setRoom(options.room);
    }
    setMode(next);
    // `store` is read by the continue branch above. The rule that flags this
    // has caught the solo opponent stalling three times, and each time the
    // symptom was the game quietly stopping rather than anything throwing.
  }, [
    store,
    fronts,
    milestone,
    openedRoom,
    reported,
    setLevel,
    setMaxFps,
    setMode,
    setNotice,
    setOpenMap,
    setRoom,
    setRunLevel,
    setScenario,
    setSeed,
    setTeams,
    setTuning
  ]);

  /**
   * Open a level in place of the board — the dev panel's door (`LoadLevel`).
   *
   * Hands the loader effect a source the way Restart hands it back its own:
   * cleared and set, so a level opened twice in a row still re-opens. It is
   * the same effect and the same `new-game`, so the seed, the ruleset and the
   * difficulty hold — and the level becomes the run's, so Restart restarts it
   * and the save carries its name. A run only: outside one the shell gets no
   * handler and the panel says so.
   */
  const loadRunLevel = useCallback((source: RunSource) => {
    setRunLevel(undefined);
    window.setTimeout(() => setRunLevel(source), 0);
  }, [setRunLevel]);

  /**
   * Start the current mode again, meaning whatever it means here.
   *
   * A run restarts its level, single player re-rolls its scenario, and a
   * hot-seat or networked game gets the ordinary opening position. One button,
   * three meanings, and none of them is "throw the level away".
   */
  const restart = useCallback(() => {
    if (isRun(mode) && runLevel) {
      const id = runLevel;
      setRunLevel(undefined);
      // Cleared and set again so the loader effect re-runs even when the level
      // has not changed.
      window.setTimeout(() => setRunLevel(id), 0);
      return;
    }
    if ((mode === "solo" || mode === "watch") && scenario) {
      // The ruleset, read off the game for `map`'s reason below: Restart
      // means the same game again, and the state only carries one when it is
      // not the default — so an ordinary board sends no field at all.
      store.dispatch(
        scenarioOpening(
          scenarioById(scenario),
          seed ?? freshSeed(),
          [],
          teams,
          store.getSnapshot().state.ruleset
        )
      );
      return;
    }
    // **The same board again, read off the game rather than off the shell.**
    // A player who joined this room never had the menu that chose it, and a
    // reload throws the shell's copy away — so the state is the only place
    // both ends can agree what "New game" means here.
    store.dispatch({
      type: "new-game",
      map: store.getSnapshot().state.map,
      seed: freshSeed(),
      ruleset: store.getSnapshot().state.ruleset
    });
  }, [teams, mode, runLevel, scenario, seed, setRunLevel, store]);

  const toMenu = useCallback(() => {
    setRunLevel(undefined);
    setLevelError(undefined);
    const url = new URL(window.location.href);
    url.searchParams.delete("room");
    url.searchParams.delete("seat");
    window.history.replaceState(null, "", url);
    setRoom(undefined);
    setMode("menu");
    store.load([]);
  }, [setMode, setRoom, setRunLevel, store]);

  if (locked === undefined) return null;

  /**
   * The two public documents, **before every gate below them**.
   *
   * This is a fix as much as a feature. The invite-only landing page returned
   * first, so the privacy notice was unreachable by exactly the people it is
   * written for — somebody deciding whether to hand over an email address —
   * and the landing page's own "What we collect" link set a state that could
   * never render. A notice you can only read after agreeing is not a notice.
   *
   * Above `locked` too, so a password-gated deployment does not hide them
   * either.
   */
  if (privacy) return <PrivacyPage onClose={() => leaveDocument(setPrivacy)} />;
  if (terms) return <TermsPage onClose={() => leaveDocument(setTerms)} />;

  // **Two doors, either one** (2026-10-29): the site password, or a Google
  // account somebody approved — `allowed` on the server takes both. A correct
  // password marks the session through, or the landing page below would ask
  // the same visitor to sign in as well.
  if (locked) {
    return (
      <GatePage
        onUnlock={() => {
          setLocked(false);
          setAccess((was) => (was ? { ...was, unlocked: true } : was));
        }}
        alternative={
          access?.accounts ? (
            <SignInDoor
              pending={access.signedIn}
              email={access.email}
              reason={new URLSearchParams(window.location.search).get("signin")}
              onPrivacy={() => openDocument(PRIVACY_PATH, setPrivacy)}
              onTerms={() => openDocument(TERMS_PATH, setTerms)}
              mail={access.mail}
            />
          ) : undefined
        }
      />
    );
  }
  // Invite-only, and this visitor is not through it. Signed in but unapproved
  // is a *state of the same page* rather than a different one: the person did
  // everything right and is waiting on us, which is worth saying rather than
  // showing them a refusal.
  if (access?.accounts && !access.unlocked) {
    return (
      <LandingPage
        pending={access.signedIn}
        email={access.email}
        reason={new URLSearchParams(window.location.search).get("signin")}
        onPrivacy={() => openDocument(PRIVACY_PATH, setPrivacy)}
          onTerms={() => openDocument(TERMS_PATH, setTerms)}
        mail={access.mail}
      />
    );
  }

  /**
   * The question, put once, to a browser that is actually through a gate.
   *
   * **After the gate branch and not before it**, so it is never the first
   * thing a stranger sees — being asked to donate your games by a site that
   * has not yet let you in is the wrong order, and a player who never gets
   * through should not be recorded as having declined.
   *
   * Rendered *beside* whatever screen is up rather than instead of it, so the
   * menu is behind it and the answer does not cost a navigation. It is a MUI
   * dialog, so it does not block the main thread and Playwright can drive it.
   */
  const consentAsk = (
    <ConsentDialog open={gated && consent.unanswered} onAnswer={consent.answer} />
  );

  // Over the top of whatever it was opened from, and closing returns there —
  /**
   * Between the internal screens, and out of them.
   *
   * **One pair of callbacks for both pages**, so the address bar and the state
   * cannot disagree about which one is showing — the failure the `Close`
   * handlers already avoid by pushing the address back. In place rather than
   * as a link, or moving between them would reload the dashboard's whole
   * digest to go two inches; the `href` is still real, so a modified click
   * opens a tab. See `AdminNav`.
   */
  const goAdmin = (page: AdminPage): void => {
    const path = ADMIN_PAGES.find((entry) => entry.id === page)?.path ?? "/";
    window.history.pushState({}, "", path);
    setTuner(page === "tuner");
    setFeatures(page === "features");
    setUsers(page === "users");
  };
  /**
   * Open one of the public documents, and put it in the address bar.
   *
   * The address is the point: "where is your privacy policy" expects a URL for
   * an answer, and a page you can only reach by clicking inside an app cannot
   * be linked to or cited. Same pushState the admin screens use, so Back works
   * and a reload lands on the document rather than on the board.
   */
  const openDocument = (path: string, set: (open: boolean) => void): void => {
    window.history.pushState({}, "", path);
    set(true);
  };
  const leaveDocument = (set: (open: boolean) => void): void => {
    window.history.pushState({}, "", "/");
    set(false);
  };

  const leaveAdmin = (): void => {
    window.history.pushState({}, "", "/");
    setTuner(false);
    setFeatures(false);
    setUsers(false);
  };

  // reading the rules mid-game should not cost you the game.
  if (rules) return <RulesPage onClose={() => setRules(false)} anchor={rulesAt} />;

  // The dashboard, likewise: it is about the opponent rather than about this
  // game, so opening it must not disturb one in progress. Closing puts the
  // address back, or the next reload lands here again.
  if (tuner) {
    return <TunerPage onClose={leaveAdmin} onGo={goAdmin} />;
  }

  // The rollout board, on the same terms as the dashboard above it: about the
  // deployment rather than about this game, so opening it must not disturb one.
  if (features) {
    return <FeaturesPage onClose={leaveAdmin} onGo={goAdmin} />;
  }

  // Approving testers, on the same terms as the two above: about the
  // deployment rather than about this game.
  if (users) {
    return <UsersPage onClose={leaveAdmin} onGo={goAdmin} />;
  }

  // Before the menu, and before anything that reads a saved run: this page has
  // its own store and must not be able to touch one — the same carve-out
  // `watch-run` makes with `ownRun`, which is why it is a route rather than a
  // launch mode.
  if (watchCpu) {
    return (
      <WatchCpuPage
        onClose={() => {
          window.history.pushState({}, "", "/");
          setWatchCpu(false);
        }}
      />
    );
  }

  if (mode === "menu") {
    return (
      <>
        <ModeMenu
          catalog={game.catalog}
          onLaunch={launch}
          onScenarios={() => setMode("scenarios")}
          onRules={(anchor?: string) => {
            setRulesAt(anchor);
            setRules(true);
          }}
          error={levelError}
          telemetry={gated ? consent : undefined}
          onPrivacy={() => openDocument(PRIVACY_PATH, setPrivacy)}
          onTerms={() => openDocument(TERMS_PATH, setTerms)}
        />
        {consentAsk}
      </>
    );
  }

  if (mode === "scenarios") {
    return (
      <ScenarioScreen
        catalog={game.catalog}
        onBack={() => setMode("menu")}
        onStart={(setup) => {
          setTuning(setup.tuning);
          // **The run is not a board, so it does not carry one.** `scenario`
          // is unread on that path — `"run"` is deliberately not a scenario id
          // (see `hasScenario`), and handing one over would be the trap that
          // note exists for arriving through a different door.
          setMaxFps(setup.maxFps);
          launch(setup.run ? "watch-run" : setup.watch ? "watch" : "solo", {
            scenario: setup.scenario,
            teams: setup.teams,
            level: setup.level,
            seed: setup.seed
          });
        }}
      />
    );
  }

  // **A run does not show its board until it has one.**
  //
  // The store opens on an ordinary chess position and a level arrives a tick
  // later, asynchronously — so mounting the shell straight away drew the
  // classic eight-by-eight, framed the camera on it, and then cut to the real
  // ground. The camera no longer *eases* between those two (`frameNow`), but
  // the placeholder was still a board you saw and it is not the game.
  //
  // Waiting is the honest fix: there is nothing to show yet. Only for a run,
  // and only while the level is still coming — every other mode's opening
  // position is real the moment the store exists.
  if (isRun(mode) && runLevel && !hasLevel) {
    return <Loading />;
  }

  return (
    <GameStoreContext.Provider value={store}>
      <GameContext.Provider value={game}>
      <NetContext.Provider value={client ?? null}>
        <GameShell
          onExit={toMenu}
          onPrivacy={() => openDocument(PRIVACY_PATH, setPrivacy)}
          onRestart={restart}
          onLoadLevel={isRun(mode) ? loadRunLevel : undefined}
          scenario={
            mode === "solo" || mode === "watch"
              ? scenario
              : isRun(mode)
              ? "run"
              : undefined
          }
          level={level}
          watching={isWatching(mode)}
          teams={teams}
          tuning={tuning}
          seed={seed}
          maxFps={maxFps}
          notice={notice}
        />
      </NetContext.Provider>
      </GameContext.Provider>
    </GameStoreContext.Provider>
  );
}

/**
 * The moment between pressing Begin and the level existing.
 *
 * Deliberately not a spinner over a board: there is no board yet, and drawing
 * one — even the store's default chess position — is the thing this exists to
 * avoid. The background is the app's own, so what a player sees is the page
 * they were already on, briefly, rather than a flash of a different game.
 */
function Loading() {
  return (
    <div
      data-testid="loading"
      style={{
        height: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#14161b",
        color: "#7b839c",
        font: "600 13px/1.5 system-ui, sans-serif",
        letterSpacing: "0.08em",
        textTransform: "uppercase"
      }}
    >
      Opening the ground…
    </div>
  );
}
